// Google tag (gtag.js) for the Google Ads account — conversion measurement.
//
// Consent Mode v2. The tag is loaded on EVERY page, but it boots with all
// consent signals DENIED. In that state gtag.js runs "cookieless" — it does
// not read or write advertising cookies — which keeps us within the
// ePrivacy "prior consent" rule. When the visitor accepts the cookie banner
// we call `setGtagConsent(true)`, which upgrades the signals to 'granted'
// and lets Google use cookies for full conversion attribution.
//
// Why not "only load after Accept" (the original approach)? Google's tag
// detector/crawler never accepts cookies, so a fully consent-gated tag was
// invisible to it ("we haven't found a Google tag on your website").
// Consent Mode is Google's supported way to reconcile a GDPR consent banner
// with Ads measurement: the tag is present and detectable, conversions can
// be modelled, and nothing is stored on the device until consent.
//
// Do NOT paste Google's raw <head> snippet into `public/index.html`: it has
// no consent default and would write advertising cookies before consent.
// This module is the single, consent-aware loader.
//
// Integration doc (account, conversion actions, env vars, verification):
// docs/integrations/google-ads.md

// Build the four Consent Mode v2 signals at a single granted/denied state.
function consentSignals(value: 'granted' | 'denied') {
  return {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  };
}

function getGtag(): ((...args: any[]) => void) | null {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return typeof w.gtag === 'function' ? w.gtag : null;
}

export function initGtag(): void {
  if (typeof window === 'undefined') return;

  const id = process.env.REACT_APP_GTAG_ID;
  if (!id) {
    console.warn('Google tag ID not provided');
    return;
  }

  const w = window as any;
  if (w.__gtagInitialised) return; // Prevent double-initialization
  w.__gtagInitialised = true;

  w.dataLayer = w.dataLayer || [];
  // gtag pushes the array-like `arguments` object (not a real array) —
  // gtag.js reads it positionally. Kept verbatim from Google's snippet.
  w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };

  // Consent Mode default — MUST be queued before `config`. Everything is
  // denied until the visitor accepts. `wait_for_update` holds tags briefly
  // so they wait for the banner decision rather than firing fully denied.
  w.gtag('consent', 'default', {
    ...consentSignals('denied'),
    wait_for_update: 500,
  });

  const head = document.getElementsByTagName('head')[0];
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + id;
  head.appendChild(s);

  w.gtag('js', new Date());
  // `url_passthrough` keeps the ad-click id (gclid) flowing via the URL when
  // cookies are denied, so conversions can still be measured/modelled.
  w.gtag('config', id, { url_passthrough: true });
}

// Upgrade or revoke the Google consent signals after the visitor decides.
// Safe to call before initGtag has run (no-ops until the tag exists).
export function setGtagConsent(granted: boolean): void {
  const gtag = getGtag();
  if (!gtag) return;
  gtag('consent', 'update', consentSignals(granted ? 'granted' : 'denied'));
}

// Fire the Google Ads "account signup" conversion.
//
// Successor of the waitlist-signup conversion (the waitlist sunset
// Aug 2026): the measurable Ads outcome is now account creation. Called
// from the auth flow (features/auth/context.tsx) on a genuine NEW
// account only — both the email/password signup and the Google
// Sign-In `isNewUser` branch — never on plain logins.
//
// Under Consent Mode the tag is always loaded, so this fires for every
// genuine signup; Google records a full conversion if the visitor accepted
// cookies, or a cookieless/modelled one if they did not. Both respect the
// consent state set via setGtagConsent.
//
// The label + value/currency come from the conversion action in Google Ads
// (see the event snippet it generates). No-op until the label is configured.
export function trackSignupConversion(): void {
  const gtag = getGtag();
  if (!gtag) return; // tag not loaded

  const id = process.env.REACT_APP_GTAG_ID;
  const label = process.env.REACT_APP_GTAG_SIGNUP_CONVERSION_LABEL;
  if (!id || !label) {
    console.warn(
      'Google Ads signup conversion label not configured; skipping conversion event'
    );
    return;
  }

  try {
    gtag('event', 'conversion', {
      send_to: id + '/' + label,
      value: 1.0,
      currency: 'EUR',
    });
  } catch (err) {
    console.error('Google Ads conversion event failed:', err);
  }
}

// Fire the Google Ads "journey generated" conversion.
//
// Added 2026-10-28 for the Google Video campaign (launch 2026-10-30): the
// outcome that campaign buys is a trip planned, not an account. Called from
// the trip detail page (pages/TripDetailPage.tsx, `handleGenerateInitial`)
// once the initial alternative set of a NEW trip has been generated — one
// conversion per trip planned. Guests are included by design: most campaign
// visitors plan their first trip without an account, and the guest tier
// allows exactly that one generation. Never fired on page reloads, on
// viewing an existing journey, on "Explore more alternatives", or on the
// post-edit Smart Mix regeneration (those add to or rework an existing trip).
//
// Consent behaviour is the same as the signup conversion above (full or
// modelled, per setGtagConsent). No-op until the label is configured.
export function trackJourneyConversion(): void {
  const gtag = getGtag();
  if (!gtag) return; // tag not loaded

  const id = process.env.REACT_APP_GTAG_ID;
  const label = process.env.REACT_APP_GTAG_JOURNEY_CONVERSION_LABEL;
  if (!id || !label) {
    console.warn(
      'Google Ads journey conversion label not configured; skipping conversion event'
    );
    return;
  }

  try {
    gtag('event', 'conversion', {
      send_to: id + '/' + label,
      value: 1.0,
      currency: 'EUR',
    });
  } catch (err) {
    console.error('Google Ads conversion event failed:', err);
  }
}
